import React, { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  AnimatedDialog,
  AnimatedDialogScrollView,
  AnimatedDialogScrollViewMounted,
  AnimatedDialogView,
  CUSTOM_NAVIGATION_HEADER_HEIGHT,
  theme,
  useResponsive,
} from '@sg/garnish';
import { LoyaltyRewardTerms } from '@sg/shared-fe/features/loyalty';

import { Loyalty } from '@order/features/loyalty';
import {
  adjustWantedTime,
  APPLE_PAY,
  Bag,
  GOOGLE_PAY,
  sumLedgerTotal,
} from '@order/features/ordering';
import {
  CreditCardForm,
  StripePaymentButton,
  StripePaymentElement,
  StripePaymentMethod,
  type useStripePayment,
} from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';
import { ignoreTimezone } from '@order/utils';

import { useLoyaltyContent, useLoyaltyPointsRewards } from '../../../../hooks';
import {
  useBagContent,
  useExpressPayments,
  type useOrderState,
  useUpdatePaymentAmount,
  useUpdateStripeLoading,
} from '../../hooks';
import { useValidProducts } from '../../hooks';
import { BagDeliveryPreferencesForm } from '../BagDeliveryPreferencesForm';
import { BagTipping } from '../BagTipping';
import { BagAnimatedDialogHeader } from './BagAnimatedDialogHeader';

/**
 * Animated dialog that shows up when choosing options in the bag.
 */
export const BagAnimatedDialog = (props: BagAnimatedDialogProps) => {
  const {
    orderState,
    isOrderPlaybackEnabled,
    orderPlaybackConfig,
    stripePaymentConfig,
  } = props;

  const { track } = useTelemetry();
  const { currentBreakpoint, minWidth, currentHeight, match } = useResponsive();

  // ─── Flags ────────────────────────────────────────────────────────────────

  const {
    isChangingTime,
    isChangingDeliveryPreferences,
    isSubmittingOrder,
    isAddingPaymentMethod,
    isApplyingGiftCard,
    isPerformingBagAction,
    isShowingCheckoutLedger: isOnCheckoutLedger,
    isShowingOrderPlayback: isOnOrderPlayback,
    isShowingModal: isOnModal,
    isShowingNextDayOrderNotice,
    isChangingPaymentMethod,
    isShowingPaymentMethodForm,
    isShowingGiftCardForm,
    isShowingLoyaltyInfo,
    isShowingLoyaltyTerms,
    isPostOrder,
  } = orderState;

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    wantedTime,
    selectedTip,
    availableTips,
    paymentMethodId,
    cart,
    paymentMethods,
    availableCredit,
    availablePoints,
    shouldUseCredit,
    rewards,
    rewardTermsBeingShown,
    expressPayments: { canApplePay, canGooglePay },
  } = orderState;

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const {
    orderChannel = 'pickup',
    restaurantId = '',
    locationName = '',
    deliveryOrderDetail,
    deliveryPreferences,
    wantedTimes,
    ledger,
    lineItems,
    maxTipAmount,
  } = cart;

  const products = useValidProducts(lineItems);

  const { creditToBeApplied, total } = sumLedgerTotal(
    ledger,
    selectedTip,
    availableCredit,
    shouldUseCredit,
  );

  // ─── Tipping ──────────────────────────────────────────────────────────────

  const isSubmissionDisabled =
    isPerformingBagAction || selectedTip > maxTipAmount;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    goBack,
    cancelRequest,
    changeDeliveryPreferences,
    changeTime,
    changeTip,
    requestPaymentMethodChange,
    finishPaymentSelection,
    changePaymentMethod,
    changeCreditUsage,
    requestPaymentMethodForm,
    submitPaymentMethodForm,
    requestGiftCardForm,
    submitGiftCardForm,
    setAvailableExpressPayments,
    requestOrderPlayback,
    requestOrderSubmission,
  } = orderState;

  // ─── Order Playback ───────────────────────────────────────────────────────

  const isShowingCheckoutLedger =
    isOnCheckoutLedger || (isSubmittingOrder && !isOrderPlaybackEnabled);

  const isShowingOrderPlayback =
    isOnOrderPlayback ||
    (isSubmittingOrder && isOrderPlaybackEnabled) ||
    (isPostOrder && isOrderPlaybackEnabled);

  const isShowingModal = isOnModal || (isPostOrder && isOrderPlaybackEnabled);

  const withCautionPalette = useFeatureFlag(
    'CELS-4534-order-playback-cta-caution-palette-enabled',
  );

  const requestOrderSubmissionOrPlayback = useCallback(
    (paymentMethodId?: string) => {
      if (isOrderPlaybackEnabled) {
        requestOrderPlayback(paymentMethodId);

        return;
      }

      requestOrderSubmission(paymentMethodId);
    },
    [isOrderPlaybackEnabled, requestOrderPlayback, requestOrderSubmission],
  );

  const handleOrderPlaybackGoBack = useCallback(() => {
    track('bag.order-playback.go-back', { orderPlaybackConfig, withCautionPalette }); // prettier-ignore
    cancelRequest();
  }, [cancelRequest, orderPlaybackConfig, withCautionPalette, track]);

  const handleOrderPlaybackLooksGood = useCallback(() => {
    track('bag.order-playback.looks-good', { orderPlaybackConfig, withCautionPalette }); // prettier-ignore
    requestOrderSubmission();
  }, [orderPlaybackConfig, requestOrderSubmission, withCautionPalette, track]);

  useTrackEventEffect({
    name: 'bag.order-playback.view',
    payload: { orderPlaybackConfig, withCautionPalette },
    skip: !isShowingOrderPlayback,
  });

  // ─── Contentful ──────────────────────────────────────────────────────

  const {
    bagDiscountBody: discountInfo,
    homeProgressBenefitsDisclaimer: benefitsDisclaimer,
  } = useLoyaltyContent({
    availablePoints,
  });

  const {
    orderPlaybackTitle,
    orderPlaybackCta,
    placeOrderCta,
    nextDayOrderWarningRegular,
    nextDayOrderWarningOutpost,
  } = useBagContent();

  // ─── Express Payments ─────────────────────────────────────────────────────
  // Whenever the total or loading state changes, we notify the Stripe hosted frame.

  const { stripeReference, isExpressPaymentsEnabled } = useExpressPayments();

  const isPayingWithExpressPayment =
    paymentMethodId === APPLE_PAY || paymentMethodId === GOOGLE_PAY;

  const shouldShowExpressPayment =
    isShowingCheckoutLedger && isPayingWithExpressPayment;

  useUpdatePaymentAmount(stripeReference, total, isShowingCheckoutLedger);
  useUpdateStripeLoading(stripeReference, isSubmittingOrder);

  // ─── Points Rewards ───────────────────────────────────────────────────────

  const { pointsRewards, isFetchingPointsRewards, refetchPointsRewards } =
    useLoyaltyPointsRewards();

  // ─── Rewards Terms ─────────────────────────────────────────────────────────

  const rewardBeingShownTerms = useMemo(
    () => rewards.find((reward) => reward.id === rewardTermsBeingShown),
    [rewardTermsBeingShown, rewards],
  );

  // ─── Animated Dialog Scrolling ────────────────────────────────────────────
  // We track how much the dialog was scrolled to animate the header opacity.

  const scrollOffsetSV = useSharedValue(0);
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollAmount = event.nativeEvent.contentOffset.y;

      // Ignore auto scroll due to the dialog header.
      if (scrollAmount === CUSTOM_NAVIGATION_HEADER_HEIGHT) return;

      scrollOffsetSV.value = scrollAmount;
    },
    [scrollOffsetSV],
  );

  const resetScrollOffset = useCallback(() => {
    scrollOffsetSV.value = 0;
  }, [scrollOffsetSV]);

  // ─── Height Without Disclaimers ───────────────────────────────────────────
  // This section measures the checkout layout without the disclaimers at the bottom.
  // This will be the max height of the animated dialog, so disclaimers aren't immediately shown.

  const ledgerDialogHeight = useSharedValue(0);
  const ledgerContentHeight = useSharedValue(0);
  const ledgerContentStyle = useAnimatedStyle(() => ({ maxHeight: ledgerContentHeight.value }), [ledgerContentHeight]); // prettier-ignore
  const measureCheckoutWithoutDisclaimers = useCallback(
    (event: LayoutChangeEvent) => {
      const layoutHeight = event.nativeEvent.layout.height;
      const layoutWithExpressPayment = layoutHeight + theme.spacing['24'];

      const withExpressPayment = shouldShowExpressPayment
        ? layoutWithExpressPayment
        : layoutHeight;

      const withHeader = withExpressPayment + theme.spacing['18'];

      ledgerDialogHeight.value = withHeader;
      ledgerContentHeight.value = withExpressPayment;
    },
    [ledgerDialogHeight, ledgerContentHeight, shouldShowExpressPayment],
  );

  // ─── Scroll View ──────────────────────────────────────────────────────────

  const scrollViewProps = useMemo(
    () => ({
      contentContainerStyle: styles.dialogContentInnerContainer,
      automaticallyAdjustKeyboardInsets: true,
      stickyHeaderIndices: [0],
      onScroll: handleOnScroll,
    }),
    [handleOnScroll],
  );

  const scrollViewFullHeightProps = useMemo(
    () => ({
      style: styles.fullHeight,
      stickyHeaderIndices: undefined,
      automaticallyAdjustKeyboardInsets: false,
    }),
    [],
  );

  // ─── Localization ────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const dismissModal = formatMessage(messages.dismissModal);
  const nextDayOrderWarning =
    orderChannel === 'outpost'
      ? nextDayOrderWarningOutpost
      : nextDayOrderWarningRegular;

  // ─── Stripe ──────────────────────────────────────────────────────────

  const isStripePaymentsEnabled = useFeatureFlag(
    'permanent-order-stripe-payment-sdk-enabled',
  );

  const {
    isGooglePay,
    embeddedPaymentElementView,
    selectedPaymentMethod,
    selectedPaymentImage,
    selectedPaymentIcon,
    stripeError,
    confirm: confirmPayment,
  } = stripePaymentConfig ?? emptyStripePaymentConfig;

  const isGooglePayAndroid = isGooglePay && Platform.OS === 'android';

  useTrackEventEffect({
    name: 'checkout.stripe-usage',
    payload: { event: 'stripe_failure', error: stripeError },
    skip: !stripeError,
  });

  // ─── Auto Close Payment Selection ──────────────────────────────────────────
  // Whenever the payment method changes, send the finish payment selection event.
  // This event is only consumed if the state is changing payment method.

  useEffect(() => {
    if (!selectedPaymentMethod) return;
    finishPaymentSelection();
  }, [finishPaymentSelection, selectedPaymentMethod]);

  // ──────────────────────────────────────────────────────────────────────────

  const hasFullHeight =
    isShowingOrderPlayback &&
    (currentBreakpoint.isXS || (minWidth.isSM && currentHeight.isSM));

  const animatedDialogStyle = hasFullHeight
    ? styles.fullContentMargin
    : styles.largerContentMargin;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedDialog
      show={isShowingModal}
      maxHeight={isShowingCheckoutLedger ? ledgerDialogHeight : undefined}
      dismissOverlayAccessibilityLabel={dismissModal}
      style={animatedDialogStyle}
      backgroundStyle={hasFullHeight ? styles.hideBorderRadius : undefined}
      onLayout={resetScrollOffset}
      dismissModal={cancelRequest}
    >
      <SafeAreaView
        style={styles.dialogContentInnerContainer}
        edges={isShowingModal ? ['bottom'] : []}
      >
        {/* Wanted time selection */}
        {isChangingTime && wantedTime ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
              closeA11yLabel={formatMessage(messages.dismissTimeSelectionModal)} // prettier-ignore
            />

            <Bag.TimePicker
              wantedTime={wantedTime}
              wantedTimes={wantedTimes}
              changeTime={changeTime}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Delivery preferences form */}
        {isChangingDeliveryPreferences ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <BagDeliveryPreferencesForm
              deliveryOrderDetail={deliveryOrderDetail}
              deliveryPreferences={deliveryPreferences}
              onSubmit={changeDeliveryPreferences}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Stripe payment element, always mounted otherwise the SDK doesn't load. */}
        {isStripePaymentsEnabled ? (
          <AnimatedDialogScrollViewMounted
            show
            lockFocus={isChangingPaymentMethod}
            style={styles.stripePaymentElement}
            {...scrollViewProps}
          >
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
              show={isChangingPaymentMethod}
            />

            <StripePaymentElement
              show={isChangingPaymentMethod}
              embeddedPaymentElementView={embeddedPaymentElementView}
            >
              <Bag.PaymentMethodGiftCardCta onPress={requestGiftCardForm} />
            </StripePaymentElement>
          </AnimatedDialogScrollViewMounted>
        ) : null}

        {/* Legacy payment method picker */}
        {isChangingPaymentMethod && !isStripePaymentsEnabled ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <Bag.PaymentMethodPicker
              paymentMethods={paymentMethods}
              paymentMethodId={paymentMethodId}
              canApplePay={canApplePay}
              canGooglePay={canGooglePay}
              onRequestPaymentMethodForm={requestPaymentMethodForm}
              onRequestGiftCardForm={requestGiftCardForm}
              changePaymentMethod={changePaymentMethod}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Legacy payment method form */}
        {isShowingPaymentMethodForm && !isStripePaymentsEnabled ? (
          <AnimatedDialogScrollView
            {...scrollViewProps}
            {...Platform.select({ native: scrollViewFullHeightProps })}
          >
            <Bag.PaymentMethodForm onBack={goBack} onClose={cancelRequest}>
              <CreditCardForm.Add
                excludedFields={['nickname', 'isDefault']}
                isSubmitting={isAddingPaymentMethod}
                handleOnSave={submitPaymentMethodForm}
              />
            </Bag.PaymentMethodForm>
          </AnimatedDialogScrollView>
        ) : null}

        {/* Giftcard form */}
        {isShowingGiftCardForm ? (
          <AnimatedDialogScrollView
            {...scrollViewProps}
            {...Platform.select({ native: scrollViewFullHeightProps })}
          >
            <Bag.GiftCardForm
              isLoading={isApplyingGiftCard}
              onSubmit={submitGiftCardForm}
              onBack={goBack}
              onClose={cancelRequest}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Loyalty info */}
        {isShowingLoyaltyInfo ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <Loyalty.InfoBenefits
              outerContainerStyle={styles.loyaltyInfoBenefits}
              loyaltyInfoBenefits={pointsRewards}
              isLoading={isFetchingPointsRewards}
              disclaimer={benefitsDisclaimer}
              refetch={refetchPointsRewards}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Loyalty Terms */}
        {isShowingLoyaltyTerms && rewardBeingShownTerms ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <LoyaltyRewardTerms
              title={rewardBeingShownTerms.name}
              terms={rewardBeingShownTerms.terms}
              expirationDate={rewardBeingShownTerms.expirationDate}
              style={styles.termsContainer}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Next Day Order Notice */}
        {isShowingNextDayOrderNotice ? (
          <AnimatedDialogScrollView {...scrollViewProps}>
            <BagAnimatedDialogHeader
              orderState={orderState}
              scrollOffsetSV={scrollOffsetSV}
            />

            <Bag.NextDayOrderWarning
              message={nextDayOrderWarning}
              onClose={cancelRequest}
            />
          </AnimatedDialogScrollView>
        ) : null}

        {/* Order Playback */}
        {isShowingOrderPlayback ? (
          <AnimatedDialogView
            style={[
              styles.fullHeight,
              match([
                styles.orderPlaybackXS,
                currentHeight.isSM
                  ? styles.orderPlaybackSmallHeightSM
                  : styles.orderPlaybackSM,
              ]),
            ]}
          >
            <Bag.OrderPlayback
              products={products}
              wantedTime={ignoreTimezone(adjustWantedTime(wantedTime) || '')}
              locationName={locationName}
              isWantedTimeAsap={orderState.isWantedTimeAsap}
              orderChannel={orderChannel}
              isLoading={isSubmittingOrder}
              isPostOrder={isPostOrder}
              orderPlaybackTitle={orderPlaybackTitle}
              orderPlaybackCta={orderPlaybackCta}
              orderPlaybackConfig={orderPlaybackConfig}
              goBack={handleOrderPlaybackGoBack}
              confirmOrder={handleOrderPlaybackLooksGood}
            />
          </AnimatedDialogView>
        ) : null}

        {/* Checkout ledger - always mounted due to the hosted frame */}
        <AnimatedDialogScrollViewMounted
          show={isShowingCheckoutLedger}
          style={isShowingCheckoutLedger ? ledgerContentStyle : undefined}
          {...scrollViewProps}
        >
          <BagAnimatedDialogHeader
            orderState={orderState}
            scrollOffsetSV={scrollOffsetSV}
            show={isShowingCheckoutLedger}
          />

          {isShowingCheckoutLedger ? (
            <Bag.CheckoutContainer onLayout={measureCheckoutWithoutDisclaimers}>
              {isStripePaymentsEnabled ? (
                <StripePaymentMethod
                  paymentMethods={paymentMethods}
                  name={selectedPaymentMethod}
                  imageBase64={selectedPaymentImage}
                  icon={selectedPaymentIcon}
                  error={stripeError}
                  isDisabled={isSubmittingOrder}
                  isGooglePay={isGooglePay}
                  onPress={requestPaymentMethodChange}
                />
              ) : (
                <Bag.SelectedPaymentMethod
                  paymentMethodId={paymentMethodId}
                  paymentMethods={paymentMethods}
                  isDisabled={isSubmittingOrder}
                  requestPaymentMethodChange={requestPaymentMethodChange}
                  requestPaymentMethodForm={requestPaymentMethodForm}
                />
              )}

              {availableCredit > 0 ? (
                <Bag.CreditToggle
                  shouldUseCredit={shouldUseCredit}
                  availableCredit={availableCredit}
                  appliedCredit={creditToBeApplied}
                  onChange={changeCreditUsage}
                />
              ) : null}

              <BagTipping
                restaurantId={restaurantId}
                orderChannel={orderChannel}
                selectedTip={selectedTip}
                availableTips={availableTips}
                maxTipAmount={maxTipAmount}
                isDisabled={isPerformingBagAction}
                onTipChange={changeTip}
              />

              <Bag.Ledger
                ledger={ledger}
                tip={selectedTip}
                maxTipAmount={maxTipAmount}
                discountInfo={discountInfo}
                availableCredit={availableCredit}
                shouldUseCredit={shouldUseCredit}
              />

              {isStripePaymentsEnabled && !isGooglePayAndroid ? (
                <Bag.CheckoutCta
                  cta={placeOrderCta}
                  paymentMethodId={selectedPaymentMethod || paymentMethods[0]?.id} // prettier-ignore
                  isLoading={isSubmittingOrder}
                  isDisabled={isSubmissionDisabled}
                  onCheckout={confirmPayment}
                />
              ) : null}

              {isStripePaymentsEnabled && isGooglePayAndroid ? (
                <StripePaymentButton
                  isDisabled={isSubmissionDisabled}
                  onPress={confirmPayment}
                />
              ) : null}

              {shouldShowExpressPayment || isStripePaymentsEnabled ? null : (
                <Bag.CheckoutCta
                  cta={placeOrderCta}
                  paymentMethodId={paymentMethodId}
                  isLoading={isSubmittingOrder}
                  isDisabled={isSubmissionDisabled}
                  onCheckout={requestOrderSubmissionOrPlayback}
                />
              )}
            </Bag.CheckoutContainer>
          ) : null}

          {isExpressPaymentsEnabled && !isStripePaymentsEnabled ? (
            <Bag.StripeOpacityAnimation show={shouldShowExpressPayment}>
              <Bag.Stripe
                ref={stripeReference}
                setAvailableExpressPayments={setAvailableExpressPayments}
                onPaymentMethodId={requestOrderSubmissionOrPlayback}
              />
            </Bag.StripeOpacityAnimation>
          ) : null}

          {isShowingCheckoutLedger ? (
            <View style={styles.disclaimersPadding}>
              <Bag.Disclaimers orderChannel={orderChannel} />
            </View>
          ) : null}
        </AnimatedDialogScrollViewMounted>
      </SafeAreaView>
    </AnimatedDialog>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const emptyStripePaymentConfig: ReturnType<typeof useStripePayment> = {
  isGooglePay: false,
  embeddedPaymentElementView: null,
  selectedPaymentMethod: '',
  selectedPaymentImage: '',
  selectedPaymentIcon: undefined,
  stripeError: '',
  confirm: () => Promise.resolve(),
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismissTimeSelectionModal: {
    defaultMessage: 'Dismiss time selection modal',
    description: 'Bag | Animated Modal | Dismiss time selection modal',
  },
  dismissModal: {
    defaultMessage: 'Dismiss modal',
    description: 'Bag | Animated Modal | Dismiss modal',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  dialogContentInnerContainer: {
    flexGrow: 1,
  },
  disclaimersPadding: {
    padding: theme.spacing['8'],
    backgroundColor: theme.colors.LINEN,
  },
  fullContentMargin: {
    flexGrow: 1,
    marginTop: 0,
  },
  largerContentMargin: {
    marginTop: CUSTOM_NAVIGATION_HEADER_HEIGHT,
  },
  loyaltyInfoBenefits: {
    marginBottom: theme.spacing['10'],
  },
  termsContainer: {
    marginTop: theme.spacing['6'],
  },
  orderPlaybackXS: {
    flexGrow: 1,
    height: '100%',
  },
  orderPlaybackSM: {
    flexShrink: 1,
    minHeight: 640,
  },
  orderPlaybackSmallHeightSM: {
    flexGrow: 1,
  },
  stripePaymentElement: {
    maxHeight: 500,
  },
  fullHeight: {
    height: '100%',
  },
  hideBorderRadius: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagAnimatedDialogProps = {
  orderState: ReturnType<typeof useOrderState>;
  isOrderPlaybackEnabled: boolean;
  stripePaymentConfig?: ReturnType<typeof useStripePayment>;
  orderPlaybackConfig: {
    locationFadeIn: number;
    timeFadeIn: number;
    orderDetailsFadeIn: number;
    lineItemsFadeIn: number;
    confirmationFadeIn: number;
  };
};
